/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer, Observer } from 'mobx-react-lite';
import * as React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import {
    DateText,
    DismissIcon,
    FloatingToast,
    PanelToast,
    StatusIcon,
    ToastContent,
    ToastLink,
    ToastSpinner,
    ToastTitleContainer
} from 'src/luis/projects/events/components/Toast/toastStyles';
import { ToastType } from 'src/luis/projects/events/types/Toast';
import { ToastStatus } from 'src/luis/types/stores/events/ToastStatus';
import styled from 'styled-components';

export type ToastProps = {
    /**
     * The store that contains the toasts.
     */
    toast: ToastType;

    /**
     * Whether the toast is in the toasts panel
     * (floating) or not.
     */
    floating?: boolean;

    /**
     * The delay before dismissing the toast.
     */
    autoDismissDelay?: number;

    /**
     * A callback for when a toast is dismissed.
     */
    onDismiss: (toastId: string) => void;
};

const toastTheme = getScopedTheme('Toast');

const PreFormattedToastContent = styled.pre`
    overflow-x: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
`;

/**
 * Gets the icon name to display based on the current
 * toast status.
 *
 * @param status The toast status to render icon
 * accordingly to.
 */
const getIconNameByStatus = (status: ToastStatus) => {
    switch (status) {
        case 'completed':
            return 'CompletedSolid';
        case 'failed':
            return 'StatusErrorFull';
        case 'informative':
            return 'InfoSolid';
        case 'running':
        default:
            return 'ProgressLoopInner';
    }
};

/**
 * Formats the given timestamp into a time string with
 * the format of "hours:minutes AM/PM".
 *
 * @param timestamp The timestamp to format.
 * @returns A formatted string.
 */
export const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const amPm = hours >= 12 ? 'PM' : 'AM';

    hours %= 12;
    hours = hours || 12;

    const minutesString: string = minutes < 10 ? `0${minutes}` : `${minutes}`;

    return `${hours}:${minutesString} ${amPm}`;
};

/**
 * A helper function to render the body content of the
 * toast, which includes the title, body, and action buttons.
 *
 * @param toast The toast to display the data for.
 * @param onDismiss The callback for when the dismiss icon is clicked.
 * @param announcedMessage The message to be announced by the screen reader for each action.
 */
const renderToastContent = (toast: ToastType, onDismiss: () => void, announcedMessage: string) => (
    <>
        <Announced message={announcedMessage} aria-live="assertive" />

        <Stack horizontal horizontalAlign="end">
            {/*
                onMouseUp ensures that onDismiss is called with any capture focus overlays
              */}
            <DismissIcon ariaLabel="Close" iconProps={{ iconName: 'Cancel' }} onMouseUp={onDismiss} />
        </Stack>

        <Stack tokens={{ childrenGap: 12 }}>
            <Stack tokens={{ childrenGap: 12 }}>
                <ToastTitleContainer horizontal verticalAlign="center">
                    {toast.status === 'running' ? (
                        <ToastSpinner size={SpinnerSize.xSmall} />
                    ) : (
                        <StatusIcon
                            title={toast.status}
                            customProps={{ status: toast.status }}
                            iconName={toast.customIconName ?? getIconNameByStatus(toast.status)}
                        />
                    )}

                    {toast.isTitleTruncated ? (
                        <TruncatedText block variant="medium" lineHeight={toastTheme.toastTitleLineHeight} title={toast.title}>
                            {toast.title}
                        </TruncatedText>
                    ) : (
                        <LuisText
                            styles={{ root: { overflowWrap: 'anywhere' } }}
                            block
                            variant="medium"
                            lineHeight={toastTheme.toastTitleLineHeight}
                            title={toast.title}>
                            {toast.title}
                        </LuisText>
                    )}

                    <DateText variant="small">{formatDate(toast.timestamp)}</DateText>
                </ToastTitleContainer>

                {toast.content && (
                    <ToastContent>
                        <Observer>
                            {() =>
                                typeof toast.content === 'string' ? (
                                    <PreFormattedToastContent>{toast.content}</PreFormattedToastContent>
                                ) : (
                                    <span>{toast.content}</span>
                                )
                            }
                        </Observer>
                    </ToastContent>
                )}
            </Stack>

            {toast.actions.length ? (
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    {toast.actions.map((action, i) => (
                        <ToastLink key={`${toast.id}-action-${i}`} onClick={_e => action.onClick(toast.id)}>
                            {action.label}
                        </ToastLink>
                    ))}
                </Stack>
            ) : null}
        </Stack>
    </>
);

/**
 * Renders a toast that is used to show notifications
 * of progress in our application.
 *
 * @param props The input to our toast.
 */
export const Toast = observer((props: ToastProps) => {
    const { toast, onDismiss, autoDismissDelay = 5000, floating = false } = props;
    const dismissTimeout = React.useRef(-1);

    /**
     * Sets the dismissal timeout after the component
     * is rendered.
     */
    React.useEffect(() => {
        if (floating && dismissTimeout.current === -1) {
            dismissTimeout.current = window.setTimeout(() => onDismiss(toast.id), toast.autoDismissDelay ?? autoDismissDelay);
        }
    }, [toast, floating, dismissTimeout.current, onDismiss, autoDismissDelay]);
    const [announcedMessage, setAnnouncedMessage] = React.useState('');

    React.useEffect(() => {
        const message = `${toast.title} ${toast.content && typeof toast.content === 'string' ? toast.content : ''}`;
        setAnnouncedMessage(message);
    }, [toast.status, toast.content]);

    React.useEffect(() => () => clearTimeout(dismissTimeout.current), []);

    /**
     * Dismisses any current timer if exists. Resets
     * the timer to start counting dismissal when
     * the mouse leaves the toast.
     */
    const onMouseEnter = (): void => {
        if (dismissTimeout.current !== -1) {
            clearTimeout(dismissTimeout.current);
            dismissTimeout.current = -1;
        }
    };

    /**
     * Restarts the timer if it was already started
     * to dismiss the toast after some delay.
     */
    const onMouseLeave = (): void => {
        if (dismissTimeout.current === -1) {
            dismissTimeout.current = window.setTimeout(() => onDismiss(toast.id), toast.autoDismissDelay ?? autoDismissDelay);
        }
    };

    /**
     * Dismisses the toast when the dismiss icon is
     * clicked on.
     */
    const onManualDismiss = (): void => {
        if (floating && dismissTimeout.current !== -1) {
            clearTimeout(dismissTimeout.current);
        }

        onDismiss(toast.id);
    };

    const toastContent = renderToastContent(toast, onManualDismiss, announcedMessage);

    return floating ? (
        <FloatingToast
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            customProps={{ status: toast.status }}
            data-automation-id="floatingToastId">
            {toastContent}
        </FloatingToast>
    ) : (
        <PanelToast customProps={{ status: toast.status }}>{toastContent}</PanelToast>
    );
});
